// Game modes: match rules (structure, teams, rounds, respawns, loadout, ammo,
// pickups, scoring, objectives). Mirrors `core/src/mode.rs`.
//
// The canonical dump/hash must match Rust exactly. Presets live in
// `mode-presets.txt` (shared with the core via `include_str!`).

import presetText from "./mode-presets.txt?raw";

export type ModeKind =
  | "race"
  | "freestyle"
  | "ffa"
  | "tdm"
  | "classic-duel"
  | "duel"
  | "ca"
  | "wipeout"
  | "ctf"
  | "instagib";

export interface GameMode {
  capture_limit: number;
  carrier_speed: number;
  flag_return_ms: number;
  frag_limit: number;
  freeze_countdown: boolean;
  friendly_fire: boolean;
  heal_ball: boolean;
  infinite_ammo: boolean;
  kind: ModeKind;
  loadout: "map" | "pickups" | "all" | "instagib";
  objective: "none" | "ctf";
  overtime: "none" | "repeat" | "golden_frag" | "sudden_death";
  overtime_ms: number;
  pickups: boolean;
  player_collision: boolean;
  pvp_damage: boolean;
  pvp_knockback: boolean;
  respawn_escalation: "none" | "individual" | "team";
  respawn_max_ms: number;
  respawn_ms: number;
  respawn_policy: "none" | "unlimited" | "round" | "escalating";
  respawn_spawn: "random" | "far" | "near_teammate" | "team";
  respawn_steps_ms: string;
  round_countdown_ms: number;
  round_end_ms: number;
  round_limit: number;
  round_time_ms: number;
  self_damage_scale: number;
  siphonator: boolean;
  start_ammo: "none" | "start" | "max";
  start_armor: number;
  start_health: number;
  structure: "race" | "continuous" | "rounds";
  team_size: number;
  teams: number;
  time_limit_ms: number;
  time_reduction_per_frag_ms: number;
  warmup_countdown_ms: number;
  warmup_min_players: number;
  warmup_ready: boolean;
  weapons: "map" | "standard" | "all" | "rail";
}

const SCALED_KEYS = new Set<keyof GameMode>(["carrier_speed", "self_damage_scale"]);

/** Race defaults (must match the `race` preset in `mode-presets.txt`). */
const DEFAULT_MODE: GameMode = {
  capture_limit: 0,
  carrier_speed: 1,
  flag_return_ms: 30000,
  frag_limit: 0,
  freeze_countdown: false,
  friendly_fire: true,
  heal_ball: false,
  infinite_ammo: false,
  kind: "race",
  loadout: "map",
  objective: "none",
  overtime: "none",
  overtime_ms: 0,
  pickups: true,
  player_collision: false,
  pvp_damage: false,
  pvp_knockback: false,
  respawn_escalation: "none",
  respawn_max_ms: 80000,
  respawn_ms: 1000,
  respawn_policy: "unlimited",
  respawn_spawn: "random",
  respawn_steps_ms: "5000,20000,40000,60000,80000",
  round_countdown_ms: 0,
  round_end_ms: 0,
  round_limit: 0,
  round_time_ms: 0,
  self_damage_scale: 1,
  siphonator: false,
  start_ammo: "start",
  start_armor: 0,
  start_health: 100,
  structure: "race",
  team_size: 0,
  teams: 0,
  time_limit_ms: 0,
  time_reduction_per_frag_ms: 0,
  warmup_countdown_ms: 0,
  warmup_min_players: 0,
  warmup_ready: false,
  weapons: "map",
};

export interface ModePreset {
  id: string;
  label: string;
  dump: string;
  mode: GameMode;
}

function parsePresetFile(text: string): ModePreset[] {
  const out: ModePreset[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const first = line.indexOf("|");
    const second = line.indexOf("|", first + 1);
    if (first < 0 || second < 0) continue;
    const id = line.slice(0, first);
    const label = line.slice(first + 1, second);
    const dump = line.slice(second + 1);
    out.push({ id, label, dump, mode: parseModeDump(dump) });
  }
  return out;
}

export const MODE_PRESETS: ModePreset[] = parsePresetFile(presetText);

export function presetMode(id: string): GameMode {
  const p = MODE_PRESETS.find((x) => x.id === id);
  return p ? { ...p.mode } : { ...DEFAULT_MODE };
}

export function presetLabel(id: string): string {
  return MODE_PRESETS.find((x) => x.id === id)?.label ?? id;
}

export function modePresetId(mode: GameMode): string | null {
  const dump = dumpMode(mode);
  return MODE_PRESETS.find((p) => p.dump === dump)?.id ?? null;
}

export function modeLabel(mode: GameMode): string {
  const id = modePresetId(mode);
  return id ? presetLabel(id) : "Custom";
}

// ---- Canonical dump / hash (must match `GameMode::dump` / `hash`) ----------

/** Build the canonical sorted `key=value;` string. */
export function dumpMode(mode: GameMode): string {
  let out = "";
  for (const k of (Object.keys(mode) as (keyof GameMode)[]).sort()) {
    const v = mode[k];
    let s: string;
    if (typeof v === "boolean") s = v ? "1" : "0";
    else if (typeof v === "string") s = v;
    else if (SCALED_KEYS.has(k)) s = String(Math.round(v * 1e6));
    else s = String(Math.round(v));
    out += `${k}=${s};`;
  }
  return out;
}

// Dev sanity check: every preset must round-trip through the TS encoder exactly
// as the shared text file says (the Rust side asserts the same).
for (const p of MODE_PRESETS) {
  if (dumpMode(p.mode) !== p.dump) {
    console.error(`mode preset "${p.id}" is not canonical`);
  }
}

/** FNV-1a 32-bit hash of {@link dumpMode}. Must match `GameMode::hash`. */
export function hashMode(mode: GameMode): number {
  const s = dumpMode(mode);
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = (h ^ s.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Parse a canonical mode dump, merging onto the Race defaults. */
export function parseModeDump(dump: string): GameMode {
  const m: GameMode = { ...DEFAULT_MODE };
  for (const part of dump.split(";")) {
    if (!part) continue;
    const i = part.indexOf("=");
    if (i < 0) continue;
    const k = part.slice(0, i) as keyof GameMode;
    const raw = part.slice(i + 1);
    if (!(k in m)) continue;
    const cur = m[k];
    const rec = m as unknown as Record<string, string | number | boolean>;
    if (typeof cur === "boolean") {
      rec[k] = raw === "1";
    } else if (typeof cur === "string") {
      rec[k] = raw;
    } else if (SCALED_KEYS.has(k)) {
      const n = Number(raw);
      if (Number.isFinite(n)) rec[k] = n / 1e6;
    } else {
      const n = Number(raw);
      if (Number.isFinite(n)) rec[k] = n;
    }
  }
  return m;
}

// ---- Editable field schema -------------------------------------------------

export interface ModeOption {
  value: string;
  label: string;
}

export interface ModeField {
  key: keyof GameMode;
  label: string;
  kind: "number" | "select" | "toggle" | "text";
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: ModeOption[];
}

export interface ModeGroup {
  title: string;
  fields: ModeField[];
}

function opt(value: string, label: string): ModeOption {
  return { value, label };
}

export const MODE_GROUPS: ModeGroup[] = [
  {
    title: "Match",
    fields: [
      {
        key: "structure",
        label: "Structure",
        kind: "select",
        options: [opt("race", "Race"), opt("continuous", "Continuous"), opt("rounds", "Rounds")],
      },
      {
        key: "teams",
        label: "Teams",
        kind: "select",
        options: [opt("0", "None"), opt("1", "FFA"), opt("2", "2 teams")],
      },
      { key: "team_size", label: "Team size (max)", kind: "number", min: 0, max: 16, step: 1 },
      { key: "time_limit_ms", label: "Time limit", kind: "number", min: 0, max: 7200000, step: 30000, unit: "ms" },
      { key: "frag_limit", label: "Frag limit", kind: "number", min: 0, max: 1000, step: 1 },
      { key: "capture_limit", label: "Capture limit", kind: "number", min: 0, max: 100, step: 1 },
      {
        key: "objective",
        label: "Objective",
        kind: "select",
        options: [opt("none", "None"), opt("ctf", "CTF flags")],
      },
    ],
  },
  {
    title: "Warmup & rounds",
    fields: [
      { key: "warmup_min_players", label: "Warmup min players", kind: "number", min: 0, max: 32, step: 1 },
      { key: "warmup_ready", label: "Ready-up", kind: "toggle" },
      { key: "warmup_countdown_ms", label: "Warmup countdown", kind: "number", min: 0, max: 30000, step: 500, unit: "ms" },
      { key: "round_limit", label: "Rounds to win", kind: "number", min: 0, max: 50, step: 1 },
      { key: "round_time_ms", label: "Round time", kind: "number", min: 0, max: 1800000, step: 30000, unit: "ms" },
      { key: "round_countdown_ms", label: "Round countdown", kind: "number", min: 0, max: 30000, step: 500, unit: "ms" },
      { key: "round_end_ms", label: "Round end pause", kind: "number", min: 0, max: 30000, step: 500, unit: "ms" },
      { key: "freeze_countdown", label: "Freeze during countdown", kind: "toggle" },
    ],
  },
  {
    title: "Respawns",
    fields: [
      {
        key: "respawn_policy",
        label: "Policy",
        kind: "select",
        options: [
          opt("none", "None (round only)"),
          opt("unlimited", "Unlimited"),
          opt("round", "Next round"),
          opt("escalating", "Escalating"),
        ],
      },
      { key: "respawn_ms", label: "Respawn delay", kind: "number", min: 0, max: 120000, step: 100, unit: "ms" },
      {
        key: "respawn_escalation",
        label: "Escalation",
        kind: "select",
        options: [opt("none", "None"), opt("individual", "Individual"), opt("team", "Team")],
      },
      { key: "respawn_steps_ms", label: "Steps (ms, comma-separated)", kind: "text" },
      { key: "respawn_max_ms", label: "Max delay", kind: "number", min: 0, max: 300000, step: 1000, unit: "ms" },
      {
        key: "respawn_spawn",
        label: "Spawn choice",
        kind: "select",
        options: [
          opt("random", "Random"),
          opt("far", "Far from enemies"),
          opt("near_teammate", "Near teammate"),
          opt("team", "Team spawn"),
        ],
      },
    ],
  },
  {
    title: "Loadout & ammo",
    fields: [
      {
        key: "loadout",
        label: "Loadout",
        kind: "select",
        options: [
          opt("map", "From map"),
          opt("pickups", "Spawn weapon + pickups"),
          opt("all", "All weapons"),
          opt("instagib", "Instagib"),
        ],
      },
      { key: "start_health", label: "Start health", kind: "number", min: 1, max: 2000, step: 1 },
      { key: "start_armor", label: "Start armor", kind: "number", min: 0, max: 1000, step: 1 },
      {
        key: "start_ammo",
        label: "Start ammo",
        kind: "select",
        options: [opt("none", "None"), opt("start", "Per-weapon start"), opt("max", "Max")],
      },
      { key: "infinite_ammo", label: "Infinite ammo", kind: "toggle" },
      {
        key: "weapons",
        label: "Weapon set",
        kind: "select",
        options: [
          opt("map", "From map"),
          opt("standard", "Standard arena set"),
          opt("all", "Everything"),
          opt("rail", "Railgun only"),
        ],
      },
      { key: "heal_ball", label: "Healing Weeball", kind: "toggle" },
      { key: "siphonator", label: "Siphonator (last alive)", kind: "toggle" },
      { key: "pickups", label: "Map pickups", kind: "toggle" },
    ],
  },
  {
    title: "Combat",
    fields: [
      { key: "self_damage_scale", label: "Self-damage scale", kind: "number", min: 0, max: 2, step: 0.05 },
      { key: "friendly_fire", label: "Friendly fire", kind: "toggle" },
      { key: "pvp_damage", label: "PvP damage", kind: "toggle" },
      { key: "pvp_knockback", label: "PvP knockback (no damage)", kind: "toggle" },
      { key: "player_collision", label: "Player collision", kind: "toggle" },
    ],
  },
  {
    title: "Overtime & objectives",
    fields: [
      {
        key: "overtime",
        label: "Overtime",
        kind: "select",
        options: [
          opt("none", "None"),
          opt("repeat", "Repeat overtime"),
          opt("golden_frag", "Golden Frag"),
          opt("sudden_death", "Sudden death"),
        ],
      },
      { key: "overtime_ms", label: "Overtime length", kind: "number", min: 0, max: 1800000, step: 30000, unit: "ms" },
      {
        key: "time_reduction_per_frag_ms",
        label: "Time reduction / frag diff",
        kind: "number",
        min: 0,
        max: 120000,
        step: 1000,
        unit: "ms",
      },
      { key: "flag_return_ms", label: "Flag auto-return", kind: "number", min: 0, max: 300000, step: 1000, unit: "ms" },
      { key: "carrier_speed", label: "Flag carrier speed", kind: "number", min: 0.1, max: 2, step: 0.05 },
    ],
  },
];

export function clampModeField(f: ModeField, v: number): number {
  if (f.min !== undefined) v = Math.max(f.min, v);
  if (f.max !== undefined) v = Math.min(f.max, v);
  const dp = (String(f.step ?? 1).split(".")[1] ?? "").length;
  return Number(v.toFixed(dp));
}

// ---- Named profiles (localStorage) ---------------------------------------

const PROFILES_KEY = "webrace.modes";

export type ModeProfiles = Record<string, GameMode>;

export function loadModeProfiles(): ModeProfiles {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return {};
    const out: ModeProfiles = {};
    for (const [name, value] of Object.entries(obj)) {
      if (typeof value === "string") out[name] = parseModeDump(value);
      else if (value && typeof value === "object") out[name] = parseModeDump(dumpMode(value as GameMode));
    }
    return out;
  } catch {
    return {};
  }
}

export function saveModeProfiles(p: ModeProfiles): void {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota */
  }
}
